/**
 * Identifier composition (Section 9.3).
 *
 * The project stores bare name-parts + one namespace. Everything full-id shaped
 * is DERIVED here and nowhere else, so "rename the namespace" is a single field
 * change that re-derives every id, lang-key root and texture short-name.
 */

import type { Item, ItemRef, Project, Recipe } from "./types";

/** Compose a full Bedrock identifier `namespace:name` from a bare name-part. */
export function fullId(namespace: string, namePart: string): string {
  return `${namespace}:${namePart}`;
}

/** The root used for every project-owned lang key. Equals the namespace. */
export function langRoot(namespace: string): string {
  return namespace;
}

/** The `item.<full-id>` display-name lang key for a bare item id. */
export function itemNameLangKey(namespace: string, namePart: string): string {
  return `item.${fullId(namespace, namePart)}`;
}

/** Texture short-name used in `item_texture.json` and `minecraft:icon`. */
export function textureShortName(namespace: string, namePart: string): string {
  return fullId(namespace, namePart);
}

/**
 * Guide lang-key roots (Section 9.3, guide subsystem).
 *
 * The in-game guide is category-based: the homepage (`main`) lists category
 * buttons, each category lists entry buttons, each entry is a title+body page.
 * Every key is derived from the namespace so a rename re-roots them all.
 */

/** Homepage title key, e.g. `NS.guide.main.title`. Also the guide item name. */
export function guideHomeTitleLangKey(namespace: string): string {
  return `${langRoot(namespace)}.guide.main.title`;
}

/** Homepage intro/body key, e.g. `NS.guide.main.body`. */
export function guideIntroLangKey(namespace: string): string {
  return `${langRoot(namespace)}.guide.main.body`;
}

/** Category title/body key, e.g. `NS.guide.<catId>.title`. */
export function guideCategoryLangKey(namespace: string, catId: string, field: "title" | "body"): string {
  return `${langRoot(namespace)}.guide.${catId}.${field}`;
}

/** Entry title/body key, e.g. `NS.guide.<catId>.<entryId>.title`. */
export function guideEntryLangKey(
  namespace: string,
  catId: string,
  entryId: string,
  field: "title" | "body",
): string {
  return `${langRoot(namespace)}.guide.${catId}.${entryId}.${field}`;
}

/** Back-compat alias: the book title equals the homepage title key. */
export function guideTitleLangKey(namespace: string): string {
  return guideHomeTitleLangKey(namespace);
}

/** Resolve an ItemRef to a full id under the current namespace. */
export function resolveRef(namespace: string, ref: ItemRef): string {
  return ref.kind === "vanilla" ? ref.id : fullId(namespace, ref.id);
}

/** The guide item's full identifier. */
export function guideFullId(project: Project): string {
  return fullId(project.namespace, project.guideItemId);
}

/**
 * The custom item-component id registered for opening the guide, e.g.
 * `mypack:open_guide`. Namespace-derived so a rename re-derives it in both the
 * item JSON (`minecraft:custom_components`) and the script registration.
 */
export function guideComponentId(namespace: string): string {
  return `${namespace}:open_guide`;
}

/** Item full id. */
export function itemFullId(project: Project, item: Item): string {
  return fullId(project.namespace, item.id);
}

/** Recipe full identifier. */
export function recipeFullId(project: Project, recipe: Recipe): string {
  return fullId(project.namespace, recipe.id);
}
